import { useState, useMemo } from "react";
import { Crown, ChevronDown, ChevronUp, Sparkles, AlertTriangle, Lock, Zap, Trophy, Info, Check, X, Swords, CheckCircle2, Target, UserRound } from "lucide-react";
import { Baseball, Basketball, SoccerBall, Football } from "@phosphor-icons/react";
import { nationalFlag } from "@/lib/flags";
import { teamCode } from "@/lib/teamAbbrev";
import { api } from "@/lib/api";
import ScoringRulesModal from "@/components/dashboard/ScoringRulesModal";
import TrophyTokensLegendModal from "@/components/dashboard/TrophyTokensLegendModal";
import MatchdayBreakdownModal from "@/components/dashboard/MatchdayBreakdownModal";
import MlbSeriesBreakdownModal from "@/components/dashboard/MlbSeriesBreakdownModal";
import NbaSeriesBreakdownModal from "@/components/dashboard/NbaSeriesBreakdownModal";
import NwslPredictorBreakdownModal from "@/components/dashboard/NwslPredictorBreakdownModal";
import { TeamSwatch } from "@/components/dashboard/TeamSwatch";
import { isBoostOnlyTemplate } from "@/lib/boostOnlyTemplates";
import { isFootballPlayoffsTemplate } from "@/lib/footballPlayoffsTemplates";
import { isPodiumMode, isLeaderEligible } from "@/lib/leaderboardRanking";
import { isSquaresTemplate } from "@/components/dashboard/SquaresPanel";

// iter87.199.150 → iter87.199.151 — Outcome column definitions, in
// display order (best → worst). Drives both the modal's per-outcome
// count columns (header labels below) and each team chip's color.
const OUTCOME_COLUMNS = [
    { key: "win_cover", label: "WC", color: "neon" },
    { key: "win", label: "W+NC", color: "cyber" },
    { key: "loss_cover", label: "LC", color: "volt" },
    { key: "loss", label: "MISS", color: "heat" },
];
const CLASSIC_OUTCOME_COLUMNS = [
    { key: "win", label: "W", color: "neon" },
    { key: "draw", label: "D", color: "volt" },
    { key: "loss", label: "L", color: "heat" },
];
const OUTCOME_LABELS = { win_cover: "wc", win: "w+nc", loss_cover: "lc", loss: "miss" };
const CLASSIC_OUTCOME_LABELS = { win: "w", draw: "d", loss: "l" };

function buildMatchdayBreakdownRows(row, tournament, teamsById) {
    const byRound = row?.matchday_boost_by_round || {};
    const scheduledRounds = row?.matchday_boost_scheduled_rounds || {};
    const useWeekLabel = tournament?.template === "nfl_boost_api" || tournament?.template === "nfl_season_api";
    // iter87.373 — NFL/CFB Playoffs' Playoff Predictor rounds read
    // "Round N" (there's no "week"/"matchday" concept in a 4-round
    // single-elim bracket).
    const useRoundLabel = isFootballPlayoffsTemplate(tournament?.template);
    const keys = Object.keys(byRound)
        .map((key) => ({ key, n: parseInt(key, 10) || 0 }))
        .filter(({ key }) => !!(scheduledRounds[key] || {}).all_played)
        .sort((a, b) => a.n - b.n);
    let total = 0;
    let columns = CLASSIC_OUTCOME_COLUMNS;
    const rows = keys.map(({ key, n }) => {
        const r = byRound[key] || {};
        const isSpread = "win_cover" in r;
        columns = isSpread ? OUTCOME_COLUMNS : CLASSIC_OUTCOME_COLUMNS;
        const labels = isSpread ? OUTCOME_LABELS : CLASSIC_OUTCOME_LABELS;
        const colorByKey = Object.fromEntries(columns.map((c) => [c.key, c.color]));
        const picks = (r.pick_details || []).map((d) => ({
            code: teamCode((teamsById || {})[d.team_id]?.name),
            outcomeLabel: labels[d.outcome] || "?",
            color: colorByKey[d.outcome] || "volt",
        }));
        const counts = Object.fromEntries(columns.map((c) => [c.key, r[c.key] || 0]));
        total += r.points || 0;
        const label = useRoundLabel ? `Round ${n}` : useWeekLabel ? `Week ${n}` : `MD${n}`;
        return { label, picks, counts, points: r.points || 0 };
    });
    return { rows, total, columns };
}

// iter87.199.147 → iter87.199.148 — Small pulsing info-dot trigger for
// the Matchday Breakdown modal. Intentionally icon-only (no text pill)
// per operator ask to avoid crowding the already-dense expanded row.
// Visual dot stays compact but the button's hit area is padded out
// closer to a comfortable mobile tap target.
function MatchdayBreakdownTrigger({ participantId, onClick }) {
    return (
        <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onClick(); }}
            data-testid={`matchday-breakdown-open-btn-${participantId}`}
            title="View weekly matchday breakdown"

            aria-label="View weekly matchday breakdown"
            className="inline-flex items-center justify-center w-6 h-6 -m-1 shrink-0"
        >
            <span className="inline-flex items-center justify-center w-4 h-4 rounded-full border border-sky-400/60 text-sky-300 bg-sky-500/10 animate-pulse hover:bg-sky-500/20">
                <Info className="w-2.5 h-2.5" />
            </span>
        </button>
    );

}

/**
 * iter87.13 (One UI) — Bento-styled leaderboard row.
 *
 * Aligns with the app-wide Bento language (BentoHome tiles, TodoChips):
 *   • Container + rows: `rounded-2xl border border-white/[0.08]
 *     bg-[#121212]` (Surface level-1), hover-lifts to `bg-[#161616]`.
 *   • Rank + avatar are `rounded-full` chips (matching TodoChips
 *     icon-wells and BentoHome tile heads), NOT sharp brutal squares.
 *   • Points block gets a bigger, cleaner numeral with a hairline
 *     divider on the left so the score reads instantly.
 *
 * Row anatomy (mobile-first, single component, no split trees):
 *   [rank chip] [avatar chip · username · pills · team flags]
 *   [ | points ] [chevron]
 *
 * All data-testids preserved from the previous layout so the pinned CI
 * pytest assertions (test_iter28_leaderboard_breakdown,
 * test_iter86_43_matchday_boost) keep passing.
 */
export default function LeaderboardTable({ rows, meId, tournament }) {
    const [expandedId, setExpandedId] = useState(null);
    const [scoringRulesOpen, setScoringRulesOpen] = useState(false);
    const [trophyLegendOpen, setTrophyLegendOpen] = useState(false);
    // iter87.198 — Removed the "Top 5 / All Crewmates" toggle entirely.
    // Crewmates below rank 5 couldn't see themselves without discovering
    // the subtle <select> in the header. Every Rivalry caps at 20
    // crewmates so rendering the full list has no perf concern. The
    // "All N" label stays as a total-count indicator.
    const visible = rows;

    // iter87.416 (operator-requested) — same Podium-vs-WTA/No-TT split
    // as the dashboard tile (iter87.414/.415): Podium Trophy Tokens
    // games keep the existing 1st/2nd/3rd treatment on this full
    // Leaderboard unchanged; everything else only highlights the sole
    // leader (renamed "LEADER") and gets a dotted divider separating
    // it from the rest of the field.
    const podiumMode = isPodiumMode(tournament);
    const specialCutoffRank = podiumMode ? 3 : 1;
    const lastSpecialIdx = visible.reduce((acc, r, i) => (r.rank <= specialCutoffRank ? i : acc), -1);

    const teamsById = (tournament?.teams || []).reduce((acc, t) => {
        acc[t.id] = t;
        return acc;
    }, {});
    // iter87.379 (user-requested) — NWSL Playoffs is pure pick'em with
    // no drafted roster, so its bracket teams live under `tournament.
    // nwsl.teams` instead of the generic top-level `tournament.teams`
    // (which NFL/CFB/MLB/NBA playoffs — with a drafted/projected field
    // — already populate). Merge them in so the Champion/Dark Horse
    // pick reveal below and the Playoff Predictor breakdown modal can
    // resolve a team_id to a name via the same `teamsById` map.
    (tournament?.nwsl?.teams || []).forEach((t) => { teamsById[t.id] = t; });
    // iter87.465.20 — MLS Playoffs mirrors NWSL's own-teams-collection pattern.
    (tournament?.mls?.teams || []).forEach((t) => { teamsById[t.id] = t; });

    // iter87.55 — Boost-only templates (CFB Weekly Pick'em today; NFL
    // Weekly Pick'em when it ships) render a clean rank row (no NOT
    // DRAFTED pill, no drafted-team flag list) and a 4-row / 2-col
    // spread outcome breakdown in the expansion.
    const isBoostOnly = isBoostOnlyTemplate(tournament?.template);
    // iter87.380 (user-requested) — NFL/CFB Playoffs and NWSL Playoffs
    // are pure pick'em (Dark Horse / Champion / Playoff Predictor only,
    // no draft), so — like boost-only — they never get the "NOT
    // DRAFTED" pill or the drafted-team flag list on the collapsed row.
    // iter87.479 (testing-agent-caught) — UCL Playoffs is the same pure
    // pick'em shape as NFL/CFB/NWSL/MLS Playoffs (no draft at all); it
    // was missed from this exclusion list too.
    const isNoDraftTemplate = isFootballPlayoffsTemplate(tournament?.template) || tournament?.template === "nwsl_playoffs_api" || tournament?.template === "mls_playoffs_api" || tournament?.template === "ucl_playoffs_api";
    // iter87.594 (user-requested) — Squares has no draft at all (so it
    // never belongs in the generic "NOT DRAFTED" pill either) and
    // drives its OWN status pill off `squares_roster_state` (TBD /
    // Pending Admin Confirm / Confirmed) instead of the unrelated generic
    // Trophy Tokens opt-in system, which Squares never touches.
    const isSquares = isSquaresTemplate(tournament?.template);

    // iter87.163 — Per-week 1st/2nd/3rd medal ranking for the boost-only
    // leaderboard expansion. Computed once across every row so a tie
    // in Week 3 points shares the same rank on every crewmate's card.
    // Competition ranking (1, 2, 2, 4). Only computed for boost-only
    // templates; O(rows × rounds) which is tiny.
    const boostRoundRanks = useMemo(() => {
        if (!isBoostOnly) return {};
        const rounds = new Set();
        rows.forEach((r) => {
            Object.keys(r.matchday_boost_by_round || {}).forEach((k) => rounds.add(k));
        });
        const out = {};
        rows.forEach((r) => { out[r.participant_id] = {}; });
        rounds.forEach((rk) => {
            const scored = rows
                .map((r) => ({ pid: r.participant_id, pts: ((r.matchday_boost_by_round || {})[rk]?.points ?? null) }))
                .filter((s) => s.pts !== null);
            scored.sort((a, b) => b.pts - a.pts);
            let rank = 0;
            let lastPts = null;
            let placed = 0;
            scored.forEach((s) => {
                placed += 1;
                if (s.pts !== lastPts) {
                    rank = placed;
                    lastPts = s.pts;
                }
                out[s.pid][rk] = rank;
            });
        });
        return out;
    }, [rows, isBoostOnly]);

    return (
        <div>
            {/* iter87.199.3 — Header cleaned up per operator ask. The
                info-icon button + verbose scoring-mode pill
                ("Cumulative" / "Final Position" / "Season W/D/L" /
                "Weekly Total") were collapsed into a single cyan
                "Scoring Rules" pill that opens the same modal. Total
                count label changed from "All N" to "N Players". */}
            <div className="flex items-center justify-end mb-3 gap-2 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                    {isSquares && (
                        <span
                            data-testid="leaderboard-squares-must-confirm-pill"
                            title="Only Confirmed players are in the Squares pot and count toward the leaderboard"
                            className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] leading-none uppercase font-bold tracking-wider border border-emerald-500/60 text-emerald-400 bg-emerald-500/10"
                        >
                            <Trophy className="w-2.5 h-2.5" /> Must Be Confirmed
                        </span>
                    )}
                    <button
                        type="button"
                        onClick={() => setScoringRulesOpen(true)}
                        data-testid="leaderboard-scoring-info-btn"
                        title="How scoring works for this tournament"
                        aria-label="Open Scoring Rules"
                        className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] leading-none uppercase font-bold tracking-wider border border-cyan-400/60 text-cyan-300 bg-cyan-500/10 hover:bg-cyan-500/20 hover:border-cyan-400 transition-colors"
                    >
                        <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
                        Scoring Rules
                    </button>
                    <span className="text-xs text-zinc-500" data-testid="leaderboard-total-count">{rows.length} {rows.length === 1 ? "Player" : "Players"}</span>
                </div>
            </div>

            <div className="space-y-2">
                {visible.length === 0 && (
                    <p className="text-zinc-500 text-sm text-center py-6">
                        Standings appear after the draft and first matches.
                    </p>
                )}
                {visible.map((row, idx) => {
                    const isMe = row.participant_id === meId;
                    const isOpen = expandedId === row.participant_id;

                    // Rank chip colors — gold / silver / bronze / dim (rounded-full, hairline).
                    // iter87.416 — in non-Podium mode, only rank 1 gets
                    // the gold treatment; 2nd/3rd fall back to the dim
                    // default so only the leader (and the viewer) stand out.
                    const RANK_STYLES = {
                        1: "border-cyber/60 text-cyber bg-cyber/15",
                        2: "border-white/25 text-zinc-100 bg-white/[0.06]",
                        3: "border-blaze/60 text-blaze bg-blaze/15",
                    };
                    const rankClass = (podiumMode || row.rank === 1 ? RANK_STYLES[row.rank] : null) || "border-white/[0.10] text-zinc-400 bg-white/[0.03]";

                    // Podium halo — soft glow + hairline ring for the top three
                    // so ranks 1-3 pop in shareable screenshots without shouting.
                    // Guard: skip when the row has 0 points so day-1 all-tied
                    // standings don't render four "1st" ribbons. Non-Podium mode
                    // only ever applies this to the sole leader (rank 1).
                    const podiumEligible = isLeaderEligible(row, podiumMode);
                    const PODIUM_HALO = {
                        1: "shadow-[inset_0_0_0_1px_rgba(212,246,68,0.35),0_0_28px_-8px_rgba(212,246,68,0.45)]",
                        2: "shadow-[inset_0_0_0_1px_rgba(228,228,231,0.30),0_0_24px_-8px_rgba(228,228,231,0.28)]",
                        3: "shadow-[inset_0_0_0_1px_rgba(255,107,74,0.35),0_0_24px_-8px_rgba(255,107,74,0.38)]",
                    };
                    const halo = podiumEligible ? (PODIUM_HALO[row.rank] || "") : "";

                    // Ordinal ribbon — tiny pill floated above the rank chip.
                    // Non-Podium mode renames rank 1's pill to "Leader".
                    const ORDINAL = podiumMode ? { 1: "1st", 2: "2nd", 3: "3rd" } : { 1: "Leader" };
                    const RIBBON_TONE = {
                        1: "bg-cyber text-black",
                        2: "bg-white text-black",
                        3: "bg-blaze text-black",
                    };
                    const ordinal = podiumEligible ? ORDINAL[row.rank] : undefined;
                    const ribbonTone = RIBBON_TONE[row.rank];

                    // iter87.199.22 — Volt Podium Ribbon on the LEFT edge
                    // of every podium row. Vertical saturated color
                    // stripe (gold / cyan / bronze) that acts as a
                    // bookmark; sits behind the existing rank chip so
                    // no other layout shifts. Renders only for ranks
                    // with actual points so a day-1 all-tied board
                    // stays clean.
                    const VOLT_PODIUM_BAR = {
                        1: "bg-gradient-to-b from-cyber to-yellow-300",
                        2: "bg-gradient-to-b from-zinc-300 to-zinc-400",
                        3: "bg-gradient-to-b from-blaze to-orange-500",
                    };
                    const voltPodiumBar = podiumEligible ? VOLT_PODIUM_BAR[row.rank] : "";

                    return (
                        <div key={row.participant_id}>
                        <div data-testid={`leaderboard-row-${row.participant_id}`}>
                            <button
                                type="button"
                                data-testid={`leaderboard-expand-${row.participant_id}-btn`}
                                onClick={() => setExpandedId(isOpen ? null : row.participant_id)}
                                className={`relative overflow-hidden w-full flex items-center gap-2.5 sm:gap-3 p-2.5 sm:p-3 rounded-lg border text-left transition-all duration-150 active:scale-[0.99] ${isMe ? "border-volt/60 bg-volt/[0.08] hover:bg-volt/[0.12]" : "border-white/[0.08] bg-[#141414] hover:bg-[#181818] hover:border-white/[0.16]"} ${halo} ${isOpen ? "rounded-b-none" : ""}`}
                            >
                                {voltPodiumBar && (
                                    <span
                                        data-testid={`volt-podium-ribbon-${row.participant_id}`}
                                        className={`absolute inset-y-0 left-0 w-1.5 ${voltPodiumBar}`}
                                        aria-hidden="true"
                                    />
                                )}
                                {/* Rank chip — circular Bento pill with podium ribbon */}
                                <div className="shrink-0 relative">
                                    {ordinal && (
                                        <span
                                            data-testid={`podium-ribbon-${row.participant_id}`}
                                            className={`absolute -top-2 left-1/2 -translate-x-1/2 rounded-full ${ribbonTone} text-[8px] font-black uppercase tracking-[0.15em] px-1.5 py-[1px] shadow-[0_1px_2px_rgba(0,0,0,0.35)]`}
                                            aria-label={`${ordinal} place`}
                                        >
                                            {ordinal}
                                        </span>
                                    )}
                                    <div
                                        className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full border ${rankClass} flex items-center justify-center font-mono font-black text-sm sm:text-base`}
                                        title={row.rank ? `Rank #${row.rank}` : "No points yet"}
                                    >
                                        {row.rank || "—"}
                                    </div>
                                </div>

                                {/* Avatar chip — circular Bento well */}
                                <div
                                    className={`shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-full border flex items-center justify-center text-lg sm:text-xl ${isMe ? "border-volt/40 bg-volt/[0.06]" : "border-white/10 bg-white/[0.04]"}`}
                                    data-testid={`leaderboard-avatar-${row.participant_id}`}
                                    aria-hidden={row.avatar_emoji ? undefined : true}
                                >
                                    {row.avatar_emoji || <UserRound size={16} className="text-zinc-600" />}
                                </div>

                                {/* Identity + teams */}
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                        <p className="font-bold text-sm sm:text-base truncate max-w-[10rem] sm:max-w-none">
                                            {row.username}
                                        </p>
                                        {row.is_admin && <Crown className="w-3 h-3 text-cyber shrink-0" />}
                                        {/* iter87.199 — Trophy Tokens 4-state icon:
                                            • state=confirmed  → blue-pulsing filled trophy (in the pot)
                                            • state=opted_in   → red-pulsing outlined trophy (awaiting admin confirm)
                                            • state=none       → dim empty outline (no signal yet)
                                            • state=dropped    → blank (nothing shown)
                                            Post-finalize the awarded count pill takes precedence over all
                                            four state icons (see the `trophy_tokens_awarded > 0` branch). */}
                                        {row.trophy_tokens_awarded > 0 ? (
                                            <span
                                                data-testid={`leaderboard-trophy-awarded-${row.participant_id}`}
                                                title={`Awarded ${row.trophy_tokens_awarded} Trophy Tokens`}
                                                className="animate-trophy-glow inline-flex items-center gap-1 rounded-full bg-volt/20 border border-volt/70 text-volt text-[9px] font-bold px-2 py-0.5"
                                            >
                                                <Trophy className="w-2.5 h-2.5" /> +{row.trophy_tokens_awarded}
                                            </span>
                                        ) : isSquares ? (
                                            // iter87.594 (user-requested) — Squares' own 4-state
                                            // pill: TBD (no picks) / Pending Admin Confirm (opted in +
                                            // claimed) / Confirmed. Dropped shows nothing, same
                                            // convention as every other state here.
                                            row.squares_roster_state === "confirmed" ? (
                                                <span
                                                    data-testid={`leaderboard-squares-confirmed-${row.participant_id}`}
                                                    title="Confirmed — in the Squares pot"
                                                    className="animate-trophy-glow inline-flex items-center gap-1 rounded-full bg-emerald-500/15 border border-emerald-500/60 text-emerald-400 text-[9px] font-bold px-1.5 py-0.5"
                                                >
                                                    <Trophy className="w-2.5 h-2.5" /> CONFIRMED
                                                </span>
                                            ) : row.squares_roster_state === "claimed" ? (
                                                <span
                                                    data-testid={`leaderboard-squares-claimed-${row.participant_id}`}
                                                    title="Boxes claimed — awaiting admin confirmation"
                                                    className="animate-pulse inline-flex items-center gap-1 rounded-full bg-amber-400/15 border border-amber-400/60 text-amber-300 text-[9px] font-bold px-1.5 py-0.5"
                                                >
                                                    <Trophy className="w-2.5 h-2.5" /> PENDING ADMIN CONFIRM
                                                </span>
                                            ) : row.squares_roster_state === "tbd" ? (
                                                <span
                                                    data-testid={`leaderboard-squares-tbd-${row.participant_id}`}
                                                    title="No picks yet"
                                                    className="animate-pulse inline-flex items-center gap-1 rounded-full bg-blaze/15 border border-blaze/60 text-blaze text-[9px] font-bold px-1.5 py-0.5"
                                                >
                                                    <AlertTriangle className="w-2.5 h-2.5" /> TBD
                                                </span>
                                            ) : null
                                        ) : row.trophy_tokens_state === "confirmed" ? (
                                            <span
                                                data-testid={`leaderboard-trophy-confirmed-${row.participant_id}`}
                                                title="Confirmed for Trophy Tokens — pot goes to the tournament winner(s)"
                                                className="animate-trophy-glow inline-flex items-center rounded-full bg-volt/15 border border-volt/60 text-volt text-[9px] font-bold px-1.5 py-0.5"
                                            >
                                                <Trophy className="w-2.5 h-2.5" />
                                            </span>
                                        ) : row.trophy_tokens_state === "opted_in" ? (
                                            <span
                                                data-testid={`leaderboard-trophy-opted-in-${row.participant_id}`}
                                                title="Opted in — awaiting admin confirmation"
                                                className="animate-pulse inline-flex items-center rounded-full bg-heat/15 border border-heat/60 text-heat text-[9px] font-bold px-1.5 py-0.5"
                                            >
                                                <Trophy className="w-2.5 h-2.5" />
                                            </span>
                                        ) : row.trophy_tokens_state === "none" ? (
                                            <span
                                                data-testid={`leaderboard-trophy-none-${row.participant_id}`}
                                                title="Not in Trophy Tokens yet — admin can opt this crewmate in"
                                                className="inline-flex items-center rounded-full bg-white/[0.03] border border-white/10 text-zinc-500 text-[9px] font-bold px-1.5 py-0.5 opacity-70"
                                            >
                                                <Trophy className="w-2.5 h-2.5" />
                                            </span>
                                        ) : null}
                                        {row.picks_hidden && (row.picks_count || 0) > 0 ? (
                                            <span
                                                data-testid={`leaderboard-picks-hidden-pill-${row.participant_id}`}
                                                className="inline-flex items-center gap-1 rounded-full bg-white/[0.06] border border-white/10 text-zinc-400 text-[9px] font-bold px-2 py-0.5"
                                            >
                                                <Lock className="w-2.5 h-2.5" /> PICKS IN — HIDDEN
                                            </span>
                                        ) : row.team_breakdown.length === 0 && !isBoostOnly && !isNoDraftTemplate && !isSquares && (
                                            <span
                                                data-testid={`leaderboard-not-drafted-pill-${row.participant_id}`}
                                                title="Joined after the draft — no teams assigned yet."
                                                className="inline-flex items-center gap-1 rounded-full bg-blaze/15 border border-blaze/60 text-blaze text-[9px] font-bold px-2 py-0.5"
                                            >
                                                <AlertTriangle className="w-2.5 h-2.5" /> NOT DRAFTED
                                            </span>
                                        )}
                                    </div>
                                    {row.crew_flair && (
                                        <p
                                            data-testid={`leaderboard-flair-${row.participant_id}`}
                                            className="text-[10px] text-zinc-500 italic truncate mt-0.5"
                                        >
                                            {row.crew_flair}
                                        </p>
                                    )}
                                    {row.team_breakdown.length > 0 && !isBoostOnly && (
                                        <div className="flex flex-wrap gap-x-1.5 gap-y-0.5 mt-1 text-[10px] text-zinc-500 leading-tight">
                                            {row.team_breakdown.slice(0, 4).map(tb => (
                                                <span key={tb.team_id} className={tb.eliminated ? "text-zinc-600 line-through" : ""}>
                                                    {nationalFlag(tb.flag)} {tb.team_name}
                                                </span>
                                            ))}
                                            {row.team_breakdown.length > 4 && (
                                                <span className="text-zinc-600">+{row.team_breakdown.length - 4}</span>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Points block */}
                                <div className="shrink-0 text-right border-l border-white/[0.08] ml-1 sm:ml-2 pl-2 sm:pl-3">
                                    <div className="font-mono font-black text-2xl sm:text-3xl leading-none text-white">
                                        {row.total_points}
                                    </div>
                                    <div className="text-[9px] uppercase tracking-widest text-zinc-600 leading-none mt-1">pts</div>
                                    {row.projected_points > row.total_points && (
                                        <div
                                            data-testid={`leaderboard-projected-${row.participant_id}`}
                                            className="text-[9px] uppercase tracking-widest text-zinc-500 font-mono leading-none mt-1"
                                            title="Maximum possible total if every remaining team wins out (and bonus pick is crowned)"
                                        >
                                            → {row.projected_points}
                                        </div>
                                    )}
                                </div>

                                {isOpen
                                    ? <ChevronUp className="w-4 h-4 text-zinc-500 shrink-0" />
                                    : <ChevronDown className="w-4 h-4 text-zinc-500 shrink-0" />}
                            </button>
                            {isOpen && (
                                <BreakdownPanel row={row} teamsById={teamsById} tournament={tournament} isMe={isMe} boostRoundRanks={boostRoundRanks} />
                            )}
                        </div>
                        {idx === lastSpecialIdx && idx < visible.length - 1 && (
                            <div
                                className="my-2.5 border-t border-dashed border-white/15"
                                data-testid="leaderboard-field-divider"
                                aria-hidden="true"
                            />
                        )}
                        </div>
                    );
                })}
            </div>
            {/* iter87.199.3 — Trophy Tokens legend moved from an inline
                4-state footnote to a dedicated popup so the leaderboard
                bottom stays uncluttered. A volt pill (bottom-right) opens
                the modal containing the same brief description, legend,
                and buy-in copy. Rendered only when Trophy Tokens is
                enabled for the tournament. */}
            {tournament?.trophy_tokens_preview?.enabled && (
                <div className="mt-3 flex justify-end">
                    <button
                        type="button"
                        onClick={() => setTrophyLegendOpen(true)}
                        data-testid="leaderboard-trophy-legend-btn"
                        title="Trophy Tokens — legend & pot"
                        aria-label="Open Trophy Tokens legend"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] uppercase font-bold tracking-wider border border-volt/60 text-volt bg-volt/10 hover:bg-volt/20 hover:border-volt transition-colors"
                    >
                        <Trophy className="w-3 h-3" />
                        Trophy Tokens
                    </button>
                </div>
            )}
            <ScoringRulesModal
                open={scoringRulesOpen}
                onClose={() => setScoringRulesOpen(false)}
                tournament={tournament}
            />
            <TrophyTokensLegendModal
                open={trophyLegendOpen}
                onClose={() => setTrophyLegendOpen(false)}
                tournament={tournament}
            />
        </div>
    );
}

function BreakdownPanel({ row, teamsById, tournament, isMe, boostRoundRanks }) {
    const [mdBreakdownOpen, setMdBreakdownOpen] = useState(false);
    // iter87.244 (user-reported) — the shared breakdown modal said
    // "Matchday Breakdown" even for NFL Season Pick'em's Gameday Boost.
    const isFootballSeason = tournament?.template === "nfl_season_api";
    const isFootballPoBreakdown = isFootballPlayoffsTemplate(tournament?.template) || tournament?.template === "mls_playoffs_api";
    const mdBreakdownTitle = isFootballPoBreakdown ? "Playoff Predictor Breakdown" : isFootballSeason ? "Gameday Breakdown" : "Matchday Breakdown";
    const mdBreakdownEmptyLabel = isFootballPoBreakdown ? "No rounds completed yet." : isFootballSeason ? "No weeks completed yet." : "No matchdays completed yet.";
    const [mlbSeriesModal, setMlbSeriesModal] = useState({ open: false, rounds: [] });
    const openMlbSeriesBreakdown = () => {
        setMlbSeriesModal({ open: true, rounds: [] });
        api.getMlbSeriesPicksDetail(tournament.id, row.participant_id)
            .then((d) => setMlbSeriesModal({ open: true, rounds: d.rounds || [] }))
            .catch(() => setMlbSeriesModal({ open: true, rounds: [] }));
    };
    const [nbaSeriesModal, setNbaSeriesModal] = useState({ open: false, rounds: [] });
    const openNbaSeriesBreakdown = () => {
        setNbaSeriesModal({ open: true, rounds: [] });
        api.getNbaSeriesPicksDetail(tournament.id, row.participant_id)
            .then((d) => setNbaSeriesModal({ open: true, rounds: d.rounds || [] }))
            .catch(() => setNbaSeriesModal({ open: true, rounds: [] }));
    };
    // iter87.361 — NWSL Match Predictor breakdown (also carries its
    // own `teams_by_id` since `tournament.teams` is always empty here).
    const [nwslPredictorModal, setNwslPredictorModal] = useState({ open: false, rounds: [], teamsById: {} });
    const openNwslPredictorBreakdown = () => {
        setNwslPredictorModal({ open: true, rounds: [], teamsById: {} });
        api.getNwslPredictorDetail(tournament.id, row.participant_id)
            .then((d) => setNwslPredictorModal({ open: true, rounds: d.rounds || [], teamsById: d.teams_by_id || {} }))
            .catch(() => setNwslPredictorModal({ open: true, rounds: [], teamsById: {} }));
    };
    // iter87.465.20 — MLS Series Predictor breakdown (Round One only —
    // backend returns a flat `series` list since there's only 1
    // round, so wrap it into the same `rounds` shape MlbSeriesBreakdownModal expects).
    const [mlsSeriesModal, setMlsSeriesModal] = useState({ open: false, rounds: [] });
    const openMlsSeriesBreakdown = () => {
        setMlsSeriesModal({ open: true, rounds: [] });
        api.getMlsSeriesPicksDetail(tournament.id, row.participant_id)
            .then((d) => setMlsSeriesModal({ open: true, rounds: [{ key: "round_one", label: "Round One", series: d.series || [] }] }))
            .catch(() => setMlsSeriesModal({ open: true, rounds: [] }));
    };
    const { rows: mdRows, total: mdTotal, columns: mdColumns } = useMemo(
        () => buildMatchdayBreakdownRows(row, tournament, teamsById),
        [row, tournament, teamsById]
    );
    const bonusTeam = row.bonus_pick_team_id ? teamsById[row.bonus_pick_team_id] : null;
    // iter87.199.121 — Mirror the EXACT eligibility check
    // `scoring_helpers.compute_leaderboard` uses for `_champions_bonus`:
    // pot pick'em mode AND a creation-time `champions_pick_bonus` stamp
    // (Super Draft / NFL only). Domestic season templates (club soccer)
    // never get that stamp, so scoring already always pays 0 for them —
    // but a stray `row.bonus_pick_team_id` (e.g. saved during a brief
    // window before the tournament's format/pickem settled at creation)
    // was still rendering this whole section unconditionally. Bug: one
    // EPL "1st Half" participant had a lone Champions Pick tile showing
    // with no other participant having one, on a template that should
    // never offer this pick at all.
    const champBonusEligible = tournament?.pickem?.mode === "pot"
        && !!tournament?.pickem?.champions_pick_bonus;
    const bonusPicked = champBonusEligible && !!row.bonus_pick_team_id;
    const bonusEarned = (row.bonus_points || 0) > 0;
    const champion = (tournament?.teams || []).find(t => t.stage_reached === "champion");
    const isSeason = (tournament?.format || "").toLowerCase() === "round_robin";
    // iter87.216 (user-reported bug) — MLB's per-team breakdown was
    // rendering through the bracket-advancement layout (R32/GROUP/
    // ADVANCE columns), a holdover from the pre-iter87.213.9 knockout
    // scoring model. MLB scores per-game win/loss now (never bracket
    // advancement), so it needs the SAME record+points layout the
    // round_robin ("season") templates use, not the KO one.
    const isMlbPot = tournament?.template === "mlb_playoffs_api";
    const isNbaPot = tournament?.template === "nba_playoffs_api";
    // iter87.373 (user-reported) — NFL/CFB Playoffs were falling
    // through to the group/advance KO layout (meaningless for them —
    // no `stage_reached`, no group stage), AND their Playoff Predictor
    // sidecar points were invisible (the boost section below only
    // rendered for `isSeason`). Sibling of the MLB/NBA record layout:
    // round win points instead of a W-L record.
    const isFootballPo = isFootballPlayoffsTemplate(tournament?.template);
    const useRecordLayout = isSeason || isMlbPot || isNbaPot || isFootballPo;
    const potPickem = tournament?.pickem?.mode === "pot";
    const seasonDone = !!tournament?.completion_authorized_by;
    const isBoostOnly = isBoostOnlyTemplate(tournament?.template);
    if (row.picks_hidden) {
        return (
            <div
                data-testid={`leaderboard-breakdown-hidden-${row.participant_id}`}
                className={`rounded-b-lg border border-t-0 ${isMe ? "border-volt/60" : "border-white/[0.08]"} bg-[#0e0e0e] px-4 py-4 text-center -mt-px`}
            >
                <p className="text-xs text-zinc-500 flex items-center justify-center gap-1.5">
                    <Lock className="w-3.5 h-3.5" />
                    Club picks are hidden until picks lock — first kickoff, or the admin locks selections.
                </p>
            </div>
        );
    }
    // iter87.163 — Boost-only expansion: WEEK-BY-WEEK table.
    // Rows = each round the player picked; columns = W+C / W / L+C /
    // Miss / Points. Sticky "Week N" on the left, sticky "Points" on
    // the right for mobile horizontal scroll. Per-week medal (1st /
    // 2nd / 3rd) with tie-share ranking sourced from `boostRoundRanks`
    // computed by the parent (LeaderboardTable) across every row.
    // iter87.164 — Rows also include UPCOMING scheduled weeks (no
    // picks yet) with "--" placeholders and a status line
    // ("Not yet selected" / "N/M selected" / "Picks locked") so the
    // crew can eyeball who's already in for the next slate.
    if (isBoostOnly) {
        const oc = row.matchday_boost_outcomes || {
            win_cover: 0, win: 0, loss_cover: 0, loss: 0,
        };
        const byRound = row.matchday_boost_by_round || {};
        const scheduledRounds = row.matchday_boost_scheduled_rounds || {};
        // iter87.166 — Union of played + scheduled rounds, sorted, then
        // TRIMMED so we don't render 18 weeks of TBD placeholders for
        // NFL / CFB seasons. Rules: show every round that has already
        // started (played + in-progress) PLUS only the next upcoming
        // unstarted round. Later unstarted weeks get folded into a
        // single "+N more weeks scheduled" summary chip so the crew
        // still knows the season has more matchdays coming without
        // scrolling past a wall of dashes.
        const sortedKeys = Array.from(new Set([
            ...Object.keys(byRound),
            ...Object.keys(scheduledRounds),
        ]))
            .map((k) => ({ key: k, n: parseInt(k, 10) || 0 }))
            .sort((a, b) => a.n - b.n)
            .map((r) => r.key);
        const firstUpcomingIdx = sortedKeys.findIndex((k) => !(scheduledRounds[k] || {}).has_started);
        const allRoundKeys = firstUpcomingIdx === -1
            ? sortedKeys
            : sortedKeys.slice(0, firstUpcomingIdx + 1);
        const hiddenUpcomingCount = firstUpcomingIdx === -1
            ? 0
            : sortedKeys.length - allRoundKeys.length;
        const picksPerRound = tournament?.matchday_boost?.picks_per_round || 5;
        const totalPicks = (oc.win_cover || 0) + (oc.win || 0) + (oc.loss_cover || 0) + (oc.loss || 0);
        const totalPts = row.matchday_boost_points || 0;
        // Per-week ranks passed in by parent — { [pid]: { [roundKey]: rank } }
        const myRanks = (boostRoundRanks && boostRoundRanks[row.participant_id]) || {};
        const medal = (rank) => {
            if (rank === 1) return { label: "1st", cls: "bg-yellow-500/20 text-yellow-300 border-yellow-500/40" };
            if (rank === 2) return { label: "2nd", cls: "bg-zinc-400/20 text-zinc-200 border-zinc-400/40" };
            if (rank === 3) return { label: "3rd", cls: "bg-amber-700/20 text-amber-400 border-amber-700/40" };
            return null;
        };
        return (
            <>
            <div
                data-testid={`leaderboard-breakdown-${row.participant_id}`}
                className={`rounded-b-lg border border-t-0 ${isMe ? "border-volt/60" : "border-white/[0.08]"} bg-[#0e0e0e] px-4 py-3 -mt-px`}
            >
                <div className="flex items-center justify-between mb-2">
                    <h5 className="text-[9px] uppercase tracking-widest text-zinc-500">Weekly Pick'em breakdown</h5>
                    {mdRows.length > 0 && (
                        <div className="flex items-center gap-1.5">
                            <span className="text-[9px] uppercase tracking-widest text-zinc-500">Total <span className="text-zinc-300 font-mono">{totalPts > 0 ? `+${totalPts}` : totalPts}</span></span>
                            <MatchdayBreakdownTrigger participantId={row.participant_id} onClick={() => setMdBreakdownOpen(true)} />
                        </div>
                    )}
                </div>
                {allRoundKeys.length === 0 ? (
                    <p className="text-xs text-zinc-500 text-center py-4" data-testid={`boost-week-empty-${row.participant_id}`}>
                        Schedule not published yet. Come back once the fixtures drop.
                    </p>
                ) : (
                    <div className="overflow-x-auto -mx-4 px-4">
                        <table className="w-full min-w-[320px] text-[11px] border-separate border-spacing-0" data-testid={`boost-week-table-${row.participant_id}`}>
                            <thead>
                                <tr className="text-[9px] uppercase tracking-widest text-zinc-500">
                                    <th className="text-left pr-2 pb-1 border-b border-white/[0.06]">Week</th>
                                    <th className="text-right px-1 pb-1 border-b border-white/[0.06]" title="Win + Cover (+6)">W+C</th>
                                    <th className="text-right px-1 pb-1 border-b border-white/[0.06]" title="Win, no cover (+2)">W</th>
                                    <th className="text-right px-1 pb-1 border-b border-white/[0.06]" title="Loss + Cover (0)">L+C</th>
                                    <th className="text-right px-1 pb-1 border-b border-white/[0.06]" title="Miss (-2)">Miss</th>
                                    <th className="text-right pl-2 pb-1 border-b border-white/[0.06]">Pts</th>
                                </tr>
                            </thead>
                            <tbody>
                                {allRoundKeys.map((rk) => {
                                    const r = byRound[rk] || {};
                                    const meta = scheduledRounds[rk] || {};
                                    const hasStarted = !!meta.has_started;
                                    const pickCount = r.picks || 0;
                                    // Pre-kickoff row → placeholders + status pill (no medal)
                                    if (!hasStarted) {
                                        let statusLabel;
                                        let statusCls;
                                        if (pickCount === 0) {
                                            statusLabel = "TBD";
                                            statusCls = "bg-zinc-800/60 text-zinc-500 border-zinc-700";
                                        } else if (pickCount < picksPerRound) {
                                            statusLabel = `${pickCount}/${picksPerRound}`;
                                            statusCls = "bg-amber-500/15 text-amber-300 border-amber-500/40";
                                        } else {
                                            statusLabel = "Locked";
                                            statusCls = "bg-sky-500/15 text-sky-300 border-sky-500/40";
                                        }
                                        return (
                                            <tr key={rk} data-testid={`boost-week-row-${row.participant_id}-${rk}`}>
                                                <td className="py-1.5 pr-2 font-mono text-zinc-500 whitespace-nowrap">
                                                    <span className="mr-1.5">W{rk}</span>
                                                    <span
                                                        className={`inline-block px-1.5 py-[1px] rounded-full text-[8px] font-bold uppercase tracking-widest border ${statusCls}`}
                                                        data-testid={`boost-week-status-${row.participant_id}-${rk}`}
                                                    >
                                                        {statusLabel}
                                                    </span>
                                                </td>
                                                <td className="text-right px-1 py-1.5 font-mono text-zinc-600">--</td>
                                                <td className="text-right px-1 py-1.5 font-mono text-zinc-600">--</td>
                                                <td className="text-right px-1 py-1.5 font-mono text-zinc-600">--</td>
                                                <td className="text-right px-1 py-1.5 font-mono text-zinc-600">--</td>
                                                <td className="text-right pl-2 py-1.5 font-mono font-bold text-zinc-600">
                                                    --
                                                </td>
                                            </tr>
                                        );
                                    }
                                    // Played / in-progress row → real numbers + medal
                                    const rank = myRanks[rk];
                                    const m = medal(rank);
                                    const pts = r.points || 0;
                                    return (
                                        <tr key={rk} data-testid={`boost-week-row-${row.participant_id}-${rk}`}>
                                            <td className="py-1.5 pr-2 font-mono text-zinc-300 whitespace-nowrap">
                                                <span className="mr-1.5">W{rk}</span>
                                                {m && (
                                                    <span
                                                        className={`inline-block px-1 py-[1px] rounded-full text-[8px] font-bold uppercase tracking-widest border ${m.cls}`}
                                                        data-testid={`boost-week-medal-${row.participant_id}-${rk}`}
                                                    >
                                                        {m.label}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="text-right px-1 py-1.5 font-mono text-emerald-300">{r.win_cover || 0}</td>
                                            <td className="text-right px-1 py-1.5 font-mono text-emerald-400/80">{r.win || 0}</td>
                                            <td className="text-right px-1 py-1.5 font-mono text-zinc-400">{r.loss_cover || 0}</td>
                                            <td className="text-right px-1 py-1.5 font-mono text-heat/80">{r.loss || 0}</td>
                                            <td className={`text-right pl-2 py-1.5 font-mono font-bold ${pts > 0 ? "text-cyber" : pts < 0 ? "text-heat" : "text-zinc-500"}`}>
                                                {pts > 0 ? `+${pts}` : pts}
                                            </td>
                                        </tr>
                                    );
                                })}
                                <tr className="border-t border-white/[0.08]" data-testid={`boost-outcome-total-${row.participant_id}`}>
                                    <td className="pt-2 pr-2 font-bold uppercase tracking-widest text-[10px] text-zinc-400 border-t border-white/[0.08]">Total</td>
                                    <td className="text-right px-1 pt-2 font-mono text-white border-t border-white/[0.08]">{oc.win_cover || 0}</td>
                                    <td className="text-right px-1 pt-2 font-mono text-white border-t border-white/[0.08]">{oc.win || 0}</td>
                                    <td className="text-right px-1 pt-2 font-mono text-white border-t border-white/[0.08]">{oc.loss_cover || 0}</td>
                                    <td className="text-right px-1 pt-2 font-mono text-white border-t border-white/[0.08]">{oc.loss || 0}</td>
                                    <td className={`text-right pl-2 pt-2 font-mono font-bold border-t border-white/[0.08] ${totalPts > 0 ? "text-cyber" : totalPts < 0 ? "text-heat" : "text-zinc-500"}`}>
                                        {totalPts > 0 ? `+${totalPts}` : totalPts}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                )}
                <p className="text-[9px] uppercase tracking-widest text-zinc-600 mt-2 text-center">
                    Total picks: {totalPicks}
                    {hiddenUpcomingCount > 0 && (
                        <span className="ml-2 text-zinc-500" data-testid={`boost-more-weeks-${row.participant_id}`}>
                            · +{hiddenUpcomingCount} more {hiddenUpcomingCount === 1 ? "week" : "weeks"} scheduled
                        </span>
                    )}
                </p>
            </div>
            <MatchdayBreakdownModal
                open={mdBreakdownOpen}
                onClose={() => setMdBreakdownOpen(false)}
                playerName={row.username}
                rows={mdRows}
                totalPoints={mdTotal}
                columns={mdColumns}
                title={mdBreakdownTitle}
                emptyLabel={mdBreakdownEmptyLabel}
            />
            </>
        );
    }
    return (
        <>
        <div
            data-testid={`leaderboard-breakdown-${row.participant_id}`}
            className={`rounded-b-lg border border-t-0 ${isMe ? "border-volt/60" : "border-white/[0.08]"} bg-[#0e0e0e] px-4 py-3 space-y-3 -mt-px`}
        >
            {/* iter87.380 (user-requested) — NFL/CFB Playoffs and NWSL
                Playoffs are pure pick'em (Dark Horse / Champion / Playoff
                Predictor only, no draft) — never render the drafted
                teams section (or its "No teams drafted yet" fallback)
                for these templates. iter87.479 (user-reported) — UCL
                Playoffs is the same pure pick'em shape (no draft at
                all); it was missed from this exclusion list. */}
            {!(isFootballPo || tournament?.template === "nwsl_playoffs_api" || tournament?.template === "mls_playoffs_api" || tournament?.template === "ucl_playoffs_api") && (
            <div>
                <h5 className="text-[9px] uppercase tracking-widest text-zinc-500 mb-2">{potPickem ? (isFootballSeason ? "Picked Teams" : "Picked Clubs") : "Drafted Teams"}</h5>
                {row.team_breakdown.length === 0 && (
                    <p className="text-xs text-zinc-600">{potPickem ? "No picks yet." : "No teams drafted yet."}</p>
                )}
                {/* iter87.165 — Fixed-width numeric columns so all
                    Cheer surfaces (LeaderboardTable / GolfLeaderboard /
                    GolfStandings) share the same numeric-column
                    rhythm. Season mode: 4 cols (name/record/pts/total).
                    Non-season: 5 cols (name/stage/group/advance/total). */}
                <div className="overflow-x-auto">
                <div
                    className="space-y-1.5"
                    style={{ minWidth: useRecordLayout ? "420px" : "460px" }}
                >
                    {/* iter87.458 (user-requested) — ranked by season
                        points, highest to lowest, so the best-performing
                        pick leads the list instead of draft/pick order. */}
                    {[...row.team_breakdown].sort((a, b) => (b.total || 0) - (a.total || 0)).map(tb => (
                        <div
                            key={tb.team_id}
                            className={`grid gap-3 items-center text-xs rounded px-1 ${tb.is_power_pick ? "bg-sky-500/10 border-l-2 border-sky-400" : ""}`}
                            style={{
                                gridTemplateColumns: useRecordLayout
                                    ? "minmax(120px,1fr) 100px 100px 72px"
                                    : "minmax(120px,1fr) 72px 88px 88px 72px",
                            }}
                            data-testid={`breakdown-row-${tb.team_id}`}
                        >
                            <div className="flex items-center gap-1.5 truncate">
                                {nationalFlag(tb.flag) && <span>{nationalFlag(tb.flag)}</span>}
                                <span className={tb.eliminated ? "text-zinc-500 line-through" : tb.is_power_pick ? "text-sky-300" : "text-zinc-200"}>
                                    {tb.team_name}
                                </span>
                                {tb.is_power_pick && <Zap className="w-3 h-3 text-sky-400 shrink-0" strokeWidth={2.5} title="Power Pick" />}
                                {tb.eliminated && <span className="text-[9px] text-blaze">OUT</span>}
                            </div>
                            {useRecordLayout ? (
                                <>
                                    <div
                                        className="text-[10px] uppercase text-zinc-500 font-mono"
                                        data-testid={`breakdown-record-${tb.team_id}`}
                                        title={isFootballPo ? "Alive / eliminated this postseason" : (isMlbPot || isNbaPot) ? "Wins · Losses this postseason" : "Wins · Draws · Losses this season"}
                                    >
                                        {(() => {
                                            if (isFootballPo) return tb.eliminated ? "Eliminated" : "Alive";
                                            const team = teamsById[tb.team_id] || {};
                                            if (isMlbPot || isNbaPot || tournament?.template === "nfl_season_api") {
                                                return `${team.group_wins || 0}-${team.group_losses || 0}${team.group_draws ? `-${team.group_draws}` : ""}`;
                                            }
                                            return `${team.group_wins || 0}W · ${team.group_draws || 0}D · ${team.group_losses || 0}L`;
                                        })()}
                                    </div>
                                    <div className="text-right text-zinc-400 font-mono">
                                        {tb.group_points > 0 ? `+${tb.group_points}` : (tb.group_points || 0) < 0 ? tb.group_points : "—"}
                                        {(tb.group_power_bonus || 0) !== 0 && (
                                            <span className="text-sky-400 inline-flex items-center gap-0.5" title={`${tb.group_power_bonus > 0 ? "+" : ""}${tb.group_power_bonus} from Power Pick multiplier`}>
                                                <Zap size={9} className="shrink-0" />{tb.group_power_bonus > 0 ? "+" : ""}{tb.group_power_bonus}
                                            </span>
                                        )}
                                        <span className="block text-[9px] text-zinc-600 uppercase">{(isMlbPot || isNbaPot || isFootballPo) ? "Pts" : "Season pts"}</span>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="text-[10px] uppercase text-zinc-500">
                                        {(() => {
                                            if (tb.stage_reached === "group") return "Group";
                                            if (tb.stage_reached === "champion") return "🏆 Champ";
                                            return tb.stage_reached?.toUpperCase();
                                        })()}
                                    </div>
                                    <div className="text-right text-zinc-400 font-mono">
                                        {tb.group_points > 0 ? `+${tb.group_points}` : "—"}
                                        <span className="block text-[9px] text-zinc-600 uppercase">Group</span>
                                    </div>
                                    <div className="text-right text-zinc-400 font-mono">
                                        {tb.advancement_points > 0 ? `+${tb.advancement_points}` : "—"}
                                        <span className="block text-[9px] text-zinc-600 uppercase">Advance</span>
                                    </div>
                                </>
                            )}
                            <div className="text-right font-mono font-bold text-cyber">
                                {tb.total}
                                {!useRecordLayout && tb.projected_max_points > 0 && (
                                    <span
                                        className="block text-[9px] text-zinc-500 font-normal leading-none mt-0.5"
                                        title="Max points if this team wins out to champion"
                                    >
                                        +{tb.projected_max_points} max
                                    </span>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
                </div>
            </div>
            )}

            {bonusPicked && (
                <div className="border-t border-white/[0.06] pt-3">
                    <h5 className="text-[9px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1">
                        <Sparkles className="w-3 h-3 text-volt" /> {potPickem ? "Champions Pick" : "Champion Bonus Pick"}
                    </h5>
                    <div className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-1.5">
                            {nationalFlag(bonusTeam?.flag) && <span>{nationalFlag(bonusTeam?.flag)}</span>}
                            <span className={bonusEarned ? "text-neon font-bold" : "text-zinc-400"}>
                                {bonusTeam?.name || "Unknown"}
                            </span>
                            {(() => {
                                if (bonusEarned) return <span className="text-[9px] uppercase text-neon">{potPickem ? "topped the table!" : "won the cup!"}</span>;
                                if (potPickem) return <span className="text-[9px] uppercase text-zinc-600">{seasonDone ? "didn't top the table" : "pending — settles at season end"}</span>;
                                if (champion) return <span className="text-[9px] uppercase text-zinc-600">didn't win</span>;
                                return <span className="text-[9px] uppercase text-zinc-600">pending</span>;
                            })()}
                        </div>
                        <div className="font-mono font-bold text-cyber">
                            {bonusEarned ? `+${row.bonus_points}` : "0"}
                        </div>
                    </div>
                </div>
            )}

            {/* iter87.379 (user-requested) — Playoff Predictor moved to
                AFTER the NFL/CFB-specific Super Bowl Champion / Dark
                Horse rows below. iter87.478 (user-requested) — that
                order is now Champion → Dark Horse → Playoff Predictor
                (see the NFL/CFB blocks further down). This block now
                only covers the other Matchday-Boost-enabled templates
                (season pick'em, boost-only) that don't have their own
                champion/darkhorse rows competing for position. */}
            {isSeason && ((row.matchday_boost_picks || 0) > 0 || tournament?.matchday_boost?.enabled) && (
                <div
                    className="border-t border-white/[0.06] pt-3 flex items-center justify-between text-xs"
                    data-testid={`breakdown-boost-${row.participant_id}`}
                >
                    <span className="text-[9px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">
                        {/* iter87.122 — NFL Season Pick'em renders as "Gameday
                            Boosts" to match the football family branding
                            (iter87.115). Soccer season templates keep the
                            "Matchday Boosts" wording. iter87.373 — NFL/CFB
                            Playoffs' in-season sidecar is "Playoff Predictor"
                            (spread + over/under), its own rebrand.
                            iter87.465.26 (user-requested — "dual chrome
                            icons instead of the emoji") — icon components
                            instead of emoji characters, matching the
                            Series Predictor rows' treatment. */}
                        {isFootballPo ? <Football size={11} weight="duotone" className="shrink-0" /> : <Zap size={11} className="shrink-0" />}
                        {isFootballPo ? "Playoff Predictor" : tournament?.template === "nfl_season_api" ? "Gameday Boosts" : "Matchday Boosts"}
                        <span className="normal-case tracking-normal text-zinc-600">
                            ({row.matchday_boost_picks || 0} picks · {row.matchday_boost_hits || 0} hits)
                        </span>
                    </span>
                    <div className="flex items-center gap-1.5 shrink-0">
                        <span className={`font-mono font-bold ${(row.matchday_boost_points || 0) > 0 ? "text-volt" : "text-zinc-600"}`}>
                            +{row.matchday_boost_points || 0}
                        </span>
                        {mdRows.length > 0 && (
                            <MatchdayBreakdownTrigger participantId={row.participant_id} onClick={() => setMdBreakdownOpen(true)} />
                        )}
                    </div>
                </div>
            )}

            {/* iter87.213.7 — MLB Champions Pick + Series Predictor had
                zero visible breakdown before this (points landed silently
                inside total_points with no explanation, unlike Matchday
                Boost above). Two lines, same visual pattern. */}
            {tournament?.template === "mlb_playoffs_api" && (
                <div
                    className="border-t border-white/[0.06] pt-3 flex items-center justify-between text-xs"
                    data-testid={`breakdown-mlb-champs-${row.participant_id}`}
                >
                    <span className="text-[9px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">
                        <Trophy size={11} className="shrink-0" /> Champions Pick
                    </span>
                    <span className={`font-mono font-bold ${(row.mlb_champs_points || 0) > 0 ? "text-neon" : "text-zinc-600"}`}>
                        +{row.mlb_champs_points || 0}
                    </span>
                </div>
            )}
            {tournament?.template === "mlb_playoffs_api" && (
                <div
                    className="border-t border-white/[0.06] pt-3 flex items-center justify-between text-xs"
                    data-testid={`breakdown-mlb-series-${row.participant_id}`}
                >
                    <span className="text-[9px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">
                        <Baseball size={11} weight="duotone" className="shrink-0" /> Series Predictor
                        <span className="normal-case tracking-normal text-zinc-600">
                            ({row.mlb_series_picks || 0} picks · {row.mlb_series_hits || 0} hits)
                        </span>
                    </span>
                    <div className="flex items-center gap-1.5 shrink-0">
                        <span className={`font-mono font-bold ${(row.mlb_series_points || 0) > 0 ? "text-volt" : "text-zinc-600"}`}>
                            {(row.mlb_series_points || 0) >= 0 ? "+" : ""}{row.mlb_series_points || 0}
                        </span>
                        {(row.mlb_series_picks || 0) > 0 && (
                            <MatchdayBreakdownTrigger participantId={`mlb-series-${row.participant_id}`} onClick={openMlbSeriesBreakdown} />
                        )}
                    </div>
                </div>
            )}

            {/* iter87.225 — NBA Champions Pick + Series Predictor,
                sibling of the MLB breakdown rows above. */}
            {tournament?.template === "nba_playoffs_api" && (
                <div
                    className="border-t border-white/[0.06] pt-3 flex items-center justify-between text-xs"
                    data-testid={`breakdown-nba-champs-${row.participant_id}`}
                >
                    <span className="text-[9px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">
                        <Trophy size={11} className="shrink-0" /> Champions Pick
                    </span>
                    <span className={`font-mono font-bold ${(row.nba_champs_points || 0) > 0 ? "text-neon" : "text-zinc-600"}`}>
                        +{row.nba_champs_points || 0}
                    </span>
                </div>
            )}
            {tournament?.template === "nba_playoffs_api" && (
                <div
                    className="border-t border-white/[0.06] pt-3 flex items-center justify-between text-xs"
                    data-testid={`breakdown-nba-series-${row.participant_id}`}
                >
                    <span className="text-[9px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">
                        <Basketball size={11} weight="duotone" className="shrink-0" /> Series Predictor
                        <span className="normal-case tracking-normal text-zinc-600">
                            ({row.nba_series_picks || 0} picks · {row.nba_series_hits || 0} hits)
                        </span>
                    </span>
                    <div className="flex items-center gap-1.5 shrink-0">
                        <span className={`font-mono font-bold ${(row.nba_series_points || 0) > 0 ? "text-volt" : "text-zinc-600"}`}>
                            {(row.nba_series_points || 0) >= 0 ? "+" : ""}{row.nba_series_points || 0}
                        </span>
                        {(row.nba_series_picks || 0) > 0 && (
                            <MatchdayBreakdownTrigger participantId={`nba-series-${row.participant_id}`} onClick={openNbaSeriesBreakdown} />
                        )}
                    </div>
                </div>
            )}

            {/* iter87.361 — NWSL Playoffs: Champions Pick + Dark Horse
                Pick + Match Predictor. Pure pick'em (no drafted teams),
                so this is the participant's entire score. Match
                Predictor stays hidden (not even a breakdown trigger)
                until at least one round is fully graded. iter87.478
                (user-requested) — Champion-before-Dark-Horse, matching
                the dashboard to-do tile order (`champion` tile before
                `nwsl-darkhorse` tile in `TodoChips.jsx`). */}
            {tournament?.template === "nwsl_playoffs_api" && (
                <div
                    className="border-t border-white/[0.06] pt-3 flex items-center justify-between text-xs"
                    data-testid={`breakdown-nwsl-champion-${row.participant_id}`}
                >
                    <span className="text-[9px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">
                        <Crown size={11} className="shrink-0" /> NWSL Champion
                    </span>
                    <div className="flex items-center gap-2">
                        {(row.nwsl_champion_team_ids || []).map((tid) => teamsById[tid] && (
                            <TeamSwatch key={tid} name={teamsById[tid].name} sport="nwsl" size="w-5 h-5 text-[8px]" />
                        ))}
                        <span className={`font-mono font-bold ${(row.nwsl_champion_points || 0) > 0 ? "text-neon" : "text-zinc-600"}`}>
                            +{row.nwsl_champion_points || 0}
                        </span>
                    </div>
                </div>
            )}
            {tournament?.template === "nwsl_playoffs_api" && (
                <div
                    className="border-t border-white/[0.06] pt-3 flex items-center justify-between text-xs"
                    data-testid={`breakdown-nwsl-darkhorse-${row.participant_id}`}
                >
                    <span className="text-[9px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">
                        <Swords size={11} className="shrink-0" /> Dark Horse Pick
                    </span>
                    <div className="flex items-center gap-2">
                        {/* iter87.379 (user-requested) — revealed to the
                            field only once Round 1 locks; hidden before
                            that (backend omits the field entirely pre-lock). */}
                        {row.nwsl_darkhorse_team_id && teamsById[row.nwsl_darkhorse_team_id] && (
                            <TeamSwatch name={teamsById[row.nwsl_darkhorse_team_id].name} sport="nwsl" size="w-5 h-5 text-[8px]" />
                        )}
                        <span className={`font-mono font-bold ${(row.nwsl_darkhorse_points || 0) > 0 ? "text-neon" : "text-zinc-600"}`}>
                            +{row.nwsl_darkhorse_points || 0}
                        </span>
                    </div>
                </div>
            )}
            {tournament?.template === "nwsl_playoffs_api" && (
                <div
                    className="border-t border-white/[0.06] pt-3 flex items-center justify-between text-xs"
                    data-testid={`breakdown-nwsl-predictor-${row.participant_id}`}
                >
                    <span className="text-[9px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">
                        <SoccerBall size={11} weight="duotone" className="shrink-0" /> Playoff Predictor
                        <span className="normal-case tracking-normal text-zinc-600">
                            ({row.nwsl_predictor_picks || 0} picks · {row.nwsl_predictor_hits || 0} hits)
                        </span>
                    </span>
                    <div className="flex items-center gap-1.5 shrink-0">
                        <span className={`font-mono font-bold ${(row.nwsl_predictor_points || 0) > 0 ? "text-volt" : "text-zinc-600"}`}>
                            {(row.nwsl_predictor_points || 0) >= 0 ? "+" : ""}{row.nwsl_predictor_points || 0}
                        </span>
                        {Object.values(row.nwsl_predictor_by_round || {}).some((r) => r.picks > 0) && (
                            <MatchdayBreakdownTrigger participantId={`nwsl-predictor-${row.participant_id}`} onClick={openNwslPredictorBreakdown} />
                        )}
                    </div>
                </div>
            )}

            {/* iter87.374 (user-requested — "follow the NWSL format ...
                remove Power picks") — NFL Playoffs: Super Bowl Champion
                (up to 2 picks, hedge payout if 2) + Dark Horse Picks
                (AFC + NFC, seed 5-7 only). iter87.478 (user-requested —
                "scoring pills should follow the draft-tile order") —
                reordered to Champion → Dark Horse → Playoff Predictor,
                matching the dashboard to-do tile order (`champion` tile
                before `nfl-darkhorse` tile in `TodoChips.jsx`); supersedes
                iter87.379's Dark Horse-first ordering. */}
            {tournament?.template === "nfl_playoffs_api" && (
                <div
                    className="border-t border-white/[0.06] pt-3 flex items-center justify-between text-xs"
                    data-testid={`breakdown-nfl-champion-${row.participant_id}`}
                >
                    <span className="text-[9px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">
                        <Trophy size={11} className="shrink-0" /> Super Bowl Champion
                    </span>
                    <div className="flex items-center gap-2">
                        {(row.nfl_champion_team_ids || []).map((tid) => teamsById[tid] && (
                            <TeamSwatch key={tid} name={teamsById[tid].name} sport="nfl" size="w-5 h-5 text-[8px]" />
                        ))}
                        <span className={`font-mono font-bold ${(row.nfl_champion_points || 0) > 0 ? "text-neon" : "text-zinc-600"}`}>
                            +{row.nfl_champion_points || 0}
                        </span>
                    </div>
                </div>
            )}
            {tournament?.template === "nfl_playoffs_api" && (
                <div
                    className="border-t border-white/[0.06] pt-3 flex items-center justify-between text-xs"
                    data-testid={`breakdown-nfl-darkhorse-${row.participant_id}`}
                >
                    <span className="text-[9px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">
                        <Swords size={11} className="shrink-0" /> Dark Horse Picks
                    </span>
                    <div className="flex items-center gap-2">
                        {/* iter87.379 — revealed only once Wild Card
                            Weekend locks; hidden before that. */}
                        {["AFC", "NFC"].map((conf) => {
                            const tid = (row.nfl_darkhorse_team_ids || {})[conf];
                            return tid && teamsById[tid] ? (
                                <TeamSwatch key={conf} name={teamsById[tid].name} sport="nfl" size="w-5 h-5 text-[8px]" />
                            ) : null;
                        })}
                        <span className={`font-mono font-bold ${(row.nfl_darkhorse_points || 0) > 0 ? "text-neon" : "text-zinc-600"}`}>
                            +{row.nfl_darkhorse_points || 0}
                        </span>
                    </div>
                </div>
            )}

            {/* iter87.437 (user-requested — "mirror NFL exactly") — CFB
                Playoffs: National Champion (up to 2 picks, hedge payout
                if 2) + Dark Horse Pick (1 non-bye team, no conference
                split). Revealed only once the First Round locks.
                iter87.478 (user-requested) — Champion-before-Dark-Horse,
                matching the dashboard to-do tile order, sibling of the
                same NFL reorder above. */}
            {tournament?.template === "cfb_playoffs_api" && (
                <div
                    className="border-t border-white/[0.06] pt-3 flex items-center justify-between text-xs"
                    data-testid={`breakdown-cfb-champion-${row.participant_id}`}
                >
                    <span className="text-[9px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">
                        <Trophy size={11} className="shrink-0" /> National Champion
                    </span>
                    <div className="flex items-center gap-2">
                        {(row.cfb_champion_team_ids || []).map((tid) => teamsById[tid] && (
                            <TeamSwatch key={tid} name={teamsById[tid].name} sport="cfb" size="w-5 h-5 text-[8px]" />
                        ))}
                        <span className={`font-mono font-bold ${(row.cfb_champion_points || 0) > 0 ? "text-neon" : "text-zinc-600"}`}>
                            +{row.cfb_champion_points || 0}
                        </span>
                    </div>
                </div>
            )}
            {tournament?.template === "cfb_playoffs_api" && (
                <div
                    className="border-t border-white/[0.06] pt-3 flex items-center justify-between text-xs"
                    data-testid={`breakdown-cfb-darkhorse-${row.participant_id}`}
                >
                    <span className="text-[9px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">
                        <Swords size={11} className="shrink-0" /> Dark Horse Pick
                    </span>
                    <div className="flex items-center gap-2">
                        {row.cfb_darkhorse_team_id && teamsById[row.cfb_darkhorse_team_id] && (
                            <TeamSwatch name={teamsById[row.cfb_darkhorse_team_id].name} sport="cfb" size="w-5 h-5 text-[8px]" />
                        )}
                        <span className={`font-mono font-bold ${(row.cfb_darkhorse_points || 0) > 0 ? "text-neon" : "text-zinc-600"}`}>
                            +{row.cfb_darkhorse_points || 0}
                        </span>
                    </div>
                </div>
            )}
            {/* iter87.465.20 — MLS Playoffs: Champions Pick + Dark Horse
                Pick + Play-In Bonus + Series Predictor (Round One).
                Match Predictor (Conf Semis+) reuses the generic
                boost breakdown row right below (same as NFL/CFB).
                iter87.478 (user-requested) — Champion-before-Dark-Horse,
                matching the dashboard to-do tile order (`champion` tile
                before `mls-darkhorse` tile in `TodoChips.jsx`). */}
            {tournament?.template === "mls_playoffs_api" && (
                <div
                    className="border-t border-white/[0.06] pt-3 flex items-center justify-between text-xs"
                    data-testid={`breakdown-mls-champion-${row.participant_id}`}
                >
                    <span className="text-[9px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">
                        <Crown size={11} className="shrink-0" /> MLS Champion
                    </span>
                    <div className="flex items-center gap-2">
                        {(row.mls_champion_team_ids || []).map((tid) => teamsById[tid] && (
                            <TeamSwatch key={tid} name={teamsById[tid].name} sport="soccer" size="w-5 h-5 text-[8px]" />
                        ))}
                        <span className={`font-mono font-bold ${(row.mls_champion_points || 0) > 0 ? "text-neon" : "text-zinc-600"}`}>
                            +{row.mls_champion_points || 0}
                        </span>
                    </div>
                </div>
            )}
            {tournament?.template === "mls_playoffs_api" && (
                <div
                    className="border-t border-white/[0.06] pt-3 flex items-center justify-between text-xs"
                    data-testid={`breakdown-mls-darkhorse-${row.participant_id}`}
                >
                    <span className="text-[9px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">
                        <Swords size={11} className="shrink-0" /> Dark Horse Pick
                    </span>
                    <div className="flex items-center gap-2">
                        {row.mls_darkhorse_team_id && teamsById[row.mls_darkhorse_team_id] && (
                            <TeamSwatch name={teamsById[row.mls_darkhorse_team_id].name} sport="soccer" size="w-5 h-5 text-[8px]" />
                        )}
                        <span className={`font-mono font-bold ${(row.mls_darkhorse_points || 0) > 0 ? "text-neon" : "text-zinc-600"}`}>
                            +{row.mls_darkhorse_points || 0}
                        </span>
                    </div>
                </div>
            )}
            {tournament?.template === "mls_playoffs_api" && (
                <div
                    className="border-t border-white/[0.06] pt-3 flex items-center justify-between text-xs"
                    data-testid={`breakdown-mls-playin-${row.participant_id}`}
                >
                    <span className="text-[9px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">
                        <CheckCircle2 size={11} className="shrink-0" /> Play-In Bonus
                    </span>
                    <div className="flex items-center gap-2">
                        {["East", "West"].map((conf) => {
                            const tid = (row.mls_playin_team_ids || {})[conf];
                            if (!tid || !teamsById[tid]) return null;
                            const correct = (row.mls_playin_correct || {})[conf];
                            return (
                                <span key={conf} className="relative inline-flex">
                                    <TeamSwatch name={teamsById[tid].name} sport="soccer" size="w-5 h-5 text-[8px]" />
                                    {correct === true && <Check className="w-2.5 h-2.5 text-neon absolute -bottom-1 -right-1 bg-black rounded-full" strokeWidth={3} />}
                                    {correct === false && <X className="w-2.5 h-2.5 text-heat absolute -bottom-1 -right-1 bg-black rounded-full" strokeWidth={3} />}
                                </span>
                            );
                        })}
                        <span className={`font-mono font-bold ${(row.mls_playin_points || 0) > 0 ? "text-neon" : "text-zinc-600"}`}>
                            +{row.mls_playin_points || 0}
                        </span>
                    </div>
                </div>
            )}
            {tournament?.template === "mls_playoffs_api" && (
                <div
                    className="border-t border-white/[0.06] pt-3 flex items-center justify-between text-xs"
                    data-testid={`breakdown-mls-series-${row.participant_id}`}
                >
                    <span className="text-[9px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">
                        <Trophy size={11} className="shrink-0" /> Series Predictor
                        <span className="normal-case tracking-normal text-zinc-600">
                            ({row.mls_series_picks || 0} picks · {row.mls_series_hits || 0} hits)
                        </span>
                    </span>
                    <div className="flex items-center gap-1.5 shrink-0">
                        <span className={`font-mono font-bold ${(row.mls_series_points || 0) > 0 ? "text-volt" : "text-zinc-600"}`}>
                            {(row.mls_series_points || 0) >= 0 ? "+" : ""}{row.mls_series_points || 0}
                        </span>
                        {(row.mls_series_picks || 0) > 0 && (
                            <MatchdayBreakdownTrigger participantId={`mls-series-${row.participant_id}`} onClick={openMlsSeriesBreakdown} />
                        )}
                    </div>
                </div>
            )}
            {/* iter87.479 (user-reported) — UCL Playoffs had NO
                breakdown rows at all (fell through to the generic
                "Drafted Teams" section, now excluded above). Added to
                match ScoringRulesModal.jsx's already-correct UCL
                section order: Champion → Dark Horse → Two-Leg Predictor
                → Champions Predictor. Field names verified against
                `scoring_helpers.py` (ucl_champion_points/_team_ids,
                ucl_darkhorse_points/_team_id, ucl_advance_points/_picks/_hits,
                ucl_champions_predictor_points) — no by-round breakdown
                exists for Advance Predictor, so no drill-down trigger. */}
            {tournament?.template === "ucl_playoffs_api" && (
                <div
                    className="border-t border-white/[0.06] pt-3 flex items-center justify-between text-xs"
                    data-testid={`breakdown-ucl-champion-${row.participant_id}`}
                >
                    <span className="text-[9px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">
                        <Crown size={11} className="shrink-0" /> UCL Champion
                    </span>
                    <div className="flex items-center gap-2">
                        {(row.ucl_champion_team_ids || []).map((tid) => teamsById[tid] && (
                            <TeamSwatch key={tid} name={teamsById[tid].name} sport="soccer" size="w-5 h-5 text-[8px]" />
                        ))}
                        <span className={`font-mono font-bold ${(row.ucl_champion_points || 0) > 0 ? "text-neon" : "text-zinc-600"}`}>
                            +{row.ucl_champion_points || 0}
                        </span>
                    </div>
                </div>
            )}
            {tournament?.template === "ucl_playoffs_api" && (
                <div
                    className="border-t border-white/[0.06] pt-3 flex items-center justify-between text-xs"
                    data-testid={`breakdown-ucl-darkhorse-${row.participant_id}`}
                >
                    <span className="text-[9px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">
                        <Swords size={11} className="shrink-0" /> Dark Horse Pick
                    </span>
                    <div className="flex items-center gap-2">
                        {row.ucl_darkhorse_team_id && teamsById[row.ucl_darkhorse_team_id] && (
                            <TeamSwatch name={teamsById[row.ucl_darkhorse_team_id].name} sport="soccer" size="w-5 h-5 text-[8px]" />
                        )}
                        <span className={`font-mono font-bold ${(row.ucl_darkhorse_points || 0) > 0 ? "text-neon" : "text-zinc-600"}`}>
                            +{row.ucl_darkhorse_points || 0}
                        </span>
                    </div>
                </div>
            )}
            {tournament?.template === "ucl_playoffs_api" && (
                <div
                    className="border-t border-white/[0.06] pt-3 flex items-center justify-between text-xs"
                    data-testid={`breakdown-ucl-advance-${row.participant_id}`}
                >
                    <span className="text-[9px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">
                        <Target size={11} className="shrink-0" /> Two-Leg Predictor
                        <span className="normal-case tracking-normal text-zinc-600">
                            ({row.ucl_advance_picks || 0} {(row.ucl_advance_picks || 0) === 1 ? "pick" : "picks"} · {row.ucl_advance_hits || 0} {(row.ucl_advance_hits || 0) === 1 ? "hit" : "hits"})
                        </span>
                    </span>
                    <span className={`font-mono font-bold ${(row.ucl_advance_points || 0) > 0 ? "text-volt" : "text-zinc-600"}`}>
                        {(row.ucl_advance_points || 0) >= 0 ? "+" : ""}{row.ucl_advance_points || 0}
                    </span>
                </div>
            )}
            {tournament?.template === "ucl_playoffs_api" && (
                <div
                    className="border-t border-white/[0.06] pt-3 flex items-center justify-between text-xs"
                    data-testid={`breakdown-ucl-champions-predictor-${row.participant_id}`}
                >
                    <span className="text-[9px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">
                        <Trophy size={11} className="shrink-0" /> Champions Predictor
                    </span>
                    <span className={`font-mono font-bold ${(row.ucl_champions_predictor_points || 0) > 0 ? "text-neon" : "text-zinc-600"}`}>
                        +{row.ucl_champions_predictor_points || 0}
                    </span>
                </div>
            )}
            {(isFootballPo || tournament?.template === "mls_playoffs_api") && ((row.matchday_boost_picks || 0) > 0 || tournament?.matchday_boost?.enabled) && (
                <div
                    className="border-t border-white/[0.06] pt-3 flex items-center justify-between text-xs"
                    data-testid={`breakdown-boost-${row.participant_id}`}
                >
                    <span className="text-[9px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">
                        {tournament?.template === "mls_playoffs_api" ? <SoccerBall size={11} weight="duotone" className="shrink-0" /> : <Football size={11} weight="duotone" className="shrink-0" />}
                        Playoff Predictor
                        <span className="normal-case tracking-normal text-zinc-600">
                            ({row.matchday_boost_picks || 0} picks · {row.matchday_boost_hits || 0} hits)
                        </span>
                    </span>
                    <div className="flex items-center gap-1.5 shrink-0">
                        <span className={`font-mono font-bold ${(row.matchday_boost_points || 0) > 0 ? "text-volt" : "text-zinc-600"}`}>
                            +{row.matchday_boost_points || 0}
                        </span>
                        {mdRows.length > 0 && (
                            <MatchdayBreakdownTrigger participantId={row.participant_id} onClick={() => setMdBreakdownOpen(true)} />
                        )}
                    </div>
                </div>
            )}

            <div className="border-t border-white/[0.06] pt-2 flex items-center justify-between text-xs">
                <span className="text-[9px] uppercase tracking-widest text-zinc-500">Total</span>
                <span className="font-mono font-black text-base text-white">{row.total_points} pts</span>
            </div>
            {row.projected_points > row.total_points && row.best_case_rank && (
                <div
                    data-testid={`leaderboard-bestcase-${row.participant_id}`}
                    className="flex items-center justify-between text-[10px] uppercase tracking-widest text-zinc-500 -mt-1"
                >
                    <span>Best case</span>
                    <span className="font-mono">
                        ceiling {row.projected_points} → finish{" "}
                        <span className={row.best_case_rank === 1 ? "text-cyber font-bold" : "text-zinc-300 font-bold"}>
                            #{row.best_case_rank}
                        </span>
                        {row.best_case_rank < row.rank && (
                            <span className="ml-1 text-neon">↑{row.rank - row.best_case_rank}</span>
                        )}
                    </span>
                </div>
            )}
        </div>
        <MatchdayBreakdownModal
            open={mdBreakdownOpen}
            onClose={() => setMdBreakdownOpen(false)}
            playerName={row.username}
            rows={mdRows}
            totalPoints={mdTotal}
            columns={mdColumns}
            title={mdBreakdownTitle}
            emptyLabel={mdBreakdownEmptyLabel}
        />
        <MlbSeriesBreakdownModal
            open={mlbSeriesModal.open}
            onClose={() => setMlbSeriesModal({ open: false, rounds: [] })}
            playerName={row.username}
            rounds={mlbSeriesModal.rounds}
            teamsById={teamsById}
            totalPoints={row.mlb_series_points || 0}
        />
        <NbaSeriesBreakdownModal
            open={nbaSeriesModal.open}
            onClose={() => setNbaSeriesModal({ open: false, rounds: [] })}
            playerName={row.username}
            rounds={nbaSeriesModal.rounds}
            teamsById={teamsById}
            totalPoints={row.nba_series_points || 0}
        />
        <NwslPredictorBreakdownModal
            open={nwslPredictorModal.open}
            onClose={() => setNwslPredictorModal({ open: false, rounds: [], teamsById: {} })}
            playerName={row.username}
            rounds={nwslPredictorModal.rounds}
            teamsById={nwslPredictorModal.teamsById}
            totalPoints={row.nwsl_predictor_points || 0}
        />
        <MlbSeriesBreakdownModal
            open={mlsSeriesModal.open}
            onClose={() => setMlsSeriesModal({ open: false, rounds: [] })}
            playerName={row.username}
            rounds={mlsSeriesModal.rounds}
            teamsById={teamsById}
            totalPoints={row.mls_series_points || 0}
        />
        </>
    );
}
